import Company from '../../api/routes/Admin/CompanyApi';
import PublicCompany from '../../api/routes/CompanyApi';

const rejectAdminApiError = (err: any) => {
    if (err?.response?.status === 401) {
        return Promise.reject({
            ...err,
            response: {
                ...err.response,
                data: {
                    ...(err.response?.data || {}),
                    errorMessage:
                        err.response?.data?.errorMessage ||
                        'Admin company action is unauthorized. Please sign in with an admin account.',
                },
            },
        });
    }
    return Promise.reject(err);
};

const fetchCompanies = async (pageIdx: number, itemsPerPage: number) => {
    try {
        const { data } = await Company.fetchCompanies(pageIdx, itemsPerPage);
        return data;
    } catch (err) {
        // In local-bypass / mixed auth environments, admin endpoints may return 401.
        // Reuse existing non-admin company endpoint to keep admin screens usable.
        if (err?.response?.status === 401) {
            const { data } = await PublicCompany.fetchCompanies();
            const items = Array.isArray(data) ? data : [];
            return {
                items,
                pageIdx,
                itemsPerPage: itemsPerPage || items.length,
                totalCount: items.length,
            };
        }
        return Promise.reject(err);
    }
};

const fetchCompanyUsers = async (companyId: string, pageIdx: number, itemsPerPage: number) => {
    try {
        const { data } = await Company.fetchCompanyUsers(companyId, pageIdx, itemsPerPage);
        return data;
    } catch (err) {
        if (err?.response?.status === 401) {
            const { data } = await PublicCompany.fetchCompanyUsers(companyId);
            const items = Array.isArray(data) ? data : [];
            return {
                items,
                pageIdx,
                itemsPerPage: itemsPerPage || items.length,
                totalCount: items.length,
            };
        }
        return Promise.reject(err);
    }
};

const fetchDetailCompany = async (companyId: string) => {
    try {
        const { data } = await Company.fetchDetailCompany(companyId);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const activateCompany = async (companyId: string) => {
    try {
        const { data } = await Company.activateCompany(companyId);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const deactivateCompany = async (companyId: string) => {
    try {
        const { data } = await Company.deactivateCompany(companyId);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const verifyCompany = async (companyId: string) => {
    try {
        const { data } = await Company.verifyCompany(companyId);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const unverifyCompany = async (companyId: string) => {
    try {
        const { data } = await Company.unverifyCompany(companyId);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const createCompany = async (company: any) => {
    try {
        const { data } = await Company.createCompany(company);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const updateCompany = async (company: any) => {
    try {
        const { data } = await Company.updateCompany(company);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const removeUsersFromCompany = async (companyId, userIds) => {
    try {
        const { data } = await Company.removeUsersFromCompany(companyId, userIds);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const addUsersToCompany = async (companyId, userIds) => {
    try {
        const data = await Company.addUsersToCompany(companyId, userIds);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const searchUsersNotInCompany = async (companyId, keyword) => {
    try {
        const { data } = await Company.searchUsersNotInCompany(companyId, keyword);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const assignRoleToUserInCompany = async (companyId, userId, role) => {
    try {
        const data = await Company.assignRoleToUserInCompany(companyId, userId, role);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const addUserToCompany = async (companyId, user) => {
    try {
        const data = await Company.addUserToCompany(companyId, user);
        return data;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const deleteUser = async (userId: string) => {
    try {
        await Company.deleteUser(userId);
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const resendInvitation = async (userId: string): Promise<{ success: boolean; error?: string }> => {
    try {
        const { data } = await Company.resendInvitation(userId);
        return data ?? { success: true };
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const getInvitationLink = async (userId: string): Promise<string> => {
    try {
        const { data } = await Company.getInvitationLink(userId);
        const relative: string = data?.invitationLink ?? '';
        if (relative.startsWith('http')) return relative;
        return `${window.location.origin}${relative.startsWith('/') ? '' : '/'}${relative}`;
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const deleteCompany = async (companyId: number) => {
    try {
        await Company.deleteCompany(companyId);
    } catch (err) {
        return rejectAdminApiError(err);
    }
};

const addImageOfCompany = async (options: any, body: any) => {
    const { data } = await Company.addImageOfCompany(options, body);
    return data;
};

const fetchAllUsersFromCompany = async (companyId: string) => {
    const { data } = await Company.fetchAllUsersFromCompany(companyId);
    return data;
};

const CompanyService = {
    fetchCompanies,
    fetchCompanyUsers,
    fetchDetailCompany,
    activateCompany,
    deactivateCompany,
    verifyCompany,
    unverifyCompany,
    createCompany,
    updateCompany,
    removeUsersFromCompany,
    addUsersToCompany,
    searchUsersNotInCompany,
    addImageOfCompany,
    assignRoleToUserInCompany,
    addUserToCompany,
    fetchAllUsersFromCompany,
    deleteUser,
    resendInvitation,
    getInvitationLink,
    deleteCompany,
};
export default CompanyService;
